// src/frontend_react/src/pages/UserDashboard/AccessDataPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query'; // Import useMutation
import { notify } from '../../services/notificationService';
import { triggerBrowserDownload } from '../../utils/fileDownloadHelper';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Select from '../../components/ui/Select/Select';
import Alert from '../../components/ui/Alert/Alert';
import Card from '../../components/ui/Card/Card';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Icon from '../../components/ui/Icon/Icon';
import Spinner from '../../components/ui/Spinner/Spinner';
import Box from '../../components/ui/Box/Box';
import EmptyState from '../../components/ui/EmptyState/EmptyState';

// Local styles
import styles from './AccessDataPage.module.css';

// API services
import {
  listEncryptedFiles,
  decryptFile,
  downloadDecryptedFile,
} from '../../services/api';

// Import icons
import { FiUnlock, FiDownload, FiAlertTriangle, FiInbox } from 'react-icons/fi';


function AccessDataPage() {
  const { selectedProjectId, isLoadingDetails: isLoadingProjectContext } = useOutletContext();
  const queryClient = useQueryClient();

  const [sourceFileType, setSourceFileType] = useState('deidentified_data');
  const [filesToList, setFilesToList] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState('');
  const [decryptedFileInfo, setDecryptedFileInfo] = useState(null);
  const [isLoadingFileList, setIsLoadingFileList] = useState(false);

  const resetLocalState = useCallback(() => {
    setDecryptedFileInfo(null);
  }, []);

  const fetchFiles = useCallback(async () => {
    if (!selectedProjectId || !sourceFileType) {
      setFilesToList([]);
      setSelectedFileId('');
      return;
    }

    setIsLoadingFileList(true);
    resetLocalState();
    try {
      const response = await listEncryptedFiles(selectedProjectId, sourceFileType);
      setFilesToList(response.files || []);
      setSelectedFileId(response.files?.length > 0 ? response.files[0].id.toString() : '');
      if (response.files?.length > 0) {
        notify.success('File list loaded.');
      }
    } catch (error) {
      notify.error(error.message || 'Error fetching files.');
      setFilesToList([]);
    } finally {
      setIsLoadingFileList(false);
    }
  }, [selectedProjectId, sourceFileType, resetLocalState]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchFiles();
    }
  }, [selectedProjectId, sourceFileType, fetchFiles]);

  useEffect(() => {
    resetLocalState();
  }, [selectedFileId, resetLocalState]);
  
  const decryptMutation = useMutation({
    mutationFn: (fileId) => decryptFile(selectedProjectId, parseInt(fileId, 10)),
    onSuccess: (result) => {
      setDecryptedFileInfo({
        id: result.decrypted_output_file_id,
        filename: result.decrypted_output_file,
      });
      notify.success(`File decrypted successfully! Output file: ${result.decrypted_output_file}`);
      queryClient.invalidateQueries({ queryKey: ['files', selectedProjectId, 'decrypted_output'] });
    },
    onError: (error) => {
      notify.error(`Decryption Failed: ${error.message}`);
    }
  });

  const downloadMutation = useMutation({
    mutationFn: () => {
      if (!decryptedFileInfo?.id) {
        throw new Error('No decrypted file information available to download.');
      }
      return downloadDecryptedFile(
        selectedProjectId,
        decryptedFileInfo.id,
        decryptedFileInfo.filename
      );
    },
    onSuccess: (data) => {
      if (data.download_url) {
        triggerBrowserDownload(data.download_url, data.suggested_filename);
      } else if (data.fileBlob) {
        triggerBrowserDownload(data.fileBlob, data.filename);
      } else {
        throw new Error("Invalid response from server during download.");
      }
      notify.success('Download initiated.');
    },
    onError: (error) => {
      notify.error(error.message || 'Download failed.');
    }
  });

  if (isLoadingProjectContext) {
    return (
      <Box display="flex" justifyContent="center" py="xl">
        <Spinner label="Loading project context..." />
      </Box>
    );
  }

  if (!selectedProjectId) {
    return <Alert variant="info" title="No Project Selected">Please select a project from the workspace to access its data.</Alert>;
  }

  const fileTypeOptions = [
    { value: 'deidentified_data', label: 'Deidentified Data Files' },
    { value: 'linkage_results', label: 'Reconciliation Results' },
    { value: 'decrypted_output', label: 'Previously Decrypted Files' },
    { value: 'linkage_reports', label: 'Reconciliation Reports (Text)' },
  ];

  const filesToSelectOptions = filesToList.map((file) => ({
    value: file.id.toString(),
    label: `${file.filename_on_disk} (Processed: ${new Date(file.processed_at).toLocaleString()})`,
  }));

  return (
    <Stack spacing="lg" className={styles.accessDataContainer}>
      <Typography variant="h2" component="h1">Access & Decrypt Data</Typography>
      <Typography variant="body1" color="secondary" paragraph>
        Decrypt previously processed files, such as deidentified data or final reconciliation results.
      </Typography>

      <Card>
        <Stack p="lg" spacing="lg">
          <Select
            label="File Category to Decrypt From:"
            id="decryptSourceFileTypeSelect"
            value={sourceFileType}
            onChange={(e) => setSourceFileType(e.target.value)}
            options={fileTypeOptions}
            disabled={decryptMutation.isPending || isLoadingFileList}
          />
        </Stack>
      </Card>

      <Card mt="lg">
        {isLoadingFileList ? (
          <Box display="flex" justifyContent="center" p="xl"><Spinner size="lg" /></Box>
        ) : filesToList.length > 0 ? (
          <Stack p="lg" spacing="lg">
            <Select
              label="File to Decrypt:" id="fileToDecryptSelect"
              value={selectedFileId}
              onChange={(e) => setSelectedFileId(e.target.value)}
              options={filesToSelectOptions}
              disabled={decryptMutation.isPending}
            />
            <Button
              onClick={() => decryptMutation.mutate(selectedFileId)}
              disabled={decryptMutation.isPending || !selectedFileId}
              variant="primary"
              leftIcon={<Icon as={FiUnlock} />}
              isLoading={decryptMutation.isPending}
            >
              {decryptMutation.isPending ? 'Decrypting...' : 'Decrypt Selected File'}
            </Button>
          </Stack>
        ) : (
          <EmptyState
            icon={FiInbox}
            title="No Files Found"
            message={`There are no files available in the "${sourceFileType.replace(/_/g, ' ')}" category for this project.`}
          />
        )}
      </Card>

      {decryptedFileInfo && (
        <Card title="Decryption Successful" mt="lg">
          <Stack p="lg" spacing="md">
            <Typography>
              The decrypted file <strong>'{decryptedFileInfo.filename}'</strong> is ready for download.
            </Typography>
            <HStack>
              <Button
                onClick={() => downloadMutation.mutate()}
                disabled={downloadMutation.isPending}
                variant="primary"
                leftIcon={<Icon as={FiDownload} />}
                isLoading={downloadMutation.isPending}
              >
                {downloadMutation.isPending ? 'Preparing...' : 'Download Decrypted File'}
              </Button>
            </HStack>
            <Alert variant="warning" title="Data Handling Notice" icon={<Icon as={FiAlertTriangle} />} mt="md">
              <Typography variant="body2">
                This file contains sensitive, unencrypted data. Ensure it is stored and handled according to your organization's security policies. Securely delete it when no longer needed.
              </Typography>
            </Alert>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

export default AccessDataPage;